import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";

// Panel de fichajes (superadmin · Gestión de datos): una fila por entrada, con su salida emparejada.
//  · Filtro de fecha (un día) ampliable a rango · botón Actualizar.
//  · Filtros por columna como en Excel (lista de valores con casillas, buscador y orden A→Z / Z→A).
//  · Pulsa una fila para ver los datos técnicos del fichaje.

const p2 = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const dia = (s) => (s ? s.slice(0, 10) : "—");
const hora = (s) => (s ? s.slice(11, 16) : "—");
// dentro (verde) · fuera de sitio (rojo) · con incidencias = cualquier otro fallo: sin ubicación, QR desconocido, piso sin coordenadas (naranja)
const claseFila = (f) => {
  const es = [f.tecnico_entrada?.estado, f.tecnico_salida?.estado].filter((e) => e && e !== "manual");   // «manual» no es una incidencia de ubicación
  if (es.includes("fuera")) return "fuera";
  return es.some((e) => e !== "dentro") ? "incid" : "dentro";
};
const COLOR_FILA = { dentro: ["#f0fdf4", "#22c55e"], fuera: ["#fef2f2", "#ef4444"], incid: ["#fff7ed", "#f59e0b"] };
const TSV = (s) => String(s ?? "").replace(/[\t\r\n]+/g, " ");
// Supervisora a la que pertenece una fila: ella misma si es quien fichó; si fichó una asistenta, la supervisora del piso
const supDe = (f) => (f.rol === "supervisora" ? f.usuario : f.supervisora) || null;
const nombreDe = (f) => [f.apellidos, f.nombre].filter(Boolean).join(", ") || f.usuario;

const EXPLICACION = [
  ["precision_m", "Error del GPS del móvil en ese momento, en metros. Cuanto más bajo, más fiable es el punto."],
  ["distancia_m", "Metros entre el punto desde el que se ficha y el piso (coordenadas de su dirección)."],
  ["desv_norte_m", "Cuánto se aleja del piso hacia el norte (+) o el sur (−), en metros."],
  ["desv_este_m", "Cuánto se aleja del piso hacia el este (+) o el oeste (−), en metros."],
  ["motivo_ubicacion", "Por qué no había ubicación en el momento de fichar. Si el fichaje tiene ubicación, llegó «tarde»: la app la siguió buscando en segundo plano hasta 30 s y se guardó después."],
  ["intentos_ubic", "Si pone 2.ª lectura, la primera ubicación salió fuera del margen y la app la repitió (sin caché) antes de dar el fichaje por bueno."],
  ["margen_m", "Distancia máxima admitida para dar el fichaje por correcto: 100 m + la precisión del GPS (hasta 100 m más). Si la distancia lo supera, es «fuera»."],
];
const MOTIVO_MANUAL = { olvido: "Se le olvidó desfichar", cambio_piso: "Cambió de piso sin desfichar", superadmin: "Puesta por un superadmin" };
const MOTIVO_UBIC_TXT = { denegada: "Permiso denegado", no_disponible: "Ubicación no disponible", tiempo_agotado: "El GPS no respondió a tiempo", sin_soporte: "El navegador no da ubicación" };
const ESTADO_TXT = { manual: "Salida manual", dentro: "Dentro", fuera: "Fuera de sitio", sin_ubicacion: "Sin ubicación", qr_desconocido: "QR desconocido", piso_sin_coordenadas: "Piso sin coordenadas" };

// Columnas: valor que se muestra en el filtro, valor para ordenar y ancho mínimo
const COLS = [
  { k: "emp", t: "id_empleado", v: (f) => (f.id_empleado == null ? "—" : String(f.id_empleado)), o: (f) => Number(f.id_empleado) || 0, w: 96 },
  { k: "nom", t: "Apellidos, Nombre", v: nombreDe, o: (f) => nombreDe(f).toLowerCase(), w: 190 },
  { k: "rol", t: "Rol", v: (f) => f.rol || "—", o: (f) => f.rol || "", w: 110 },
  { k: "pid", t: "id_piso", v: (f) => (f.id_piso == null ? "—" : String(f.id_piso)), o: (f) => Number(f.id_piso) || 0, w: 80 },
  { k: "abr", t: "Abreviatura", v: (f) => f.abreviatura || "—", o: (f) => (f.abreviatura || "").toLowerCase(), w: 140 },
  { k: "de", t: "Día entrada", v: (f) => dia(f.entrada), o: (f) => f.entrada, w: 110 },
  { k: "he", t: "Hora entrada", v: (f) => hora(f.entrada), o: (f) => f.entrada.slice(11), w: 100 },
  { k: "hs", t: "Hora salida", v: (f) => (f.salida ? hora(f.salida) + (f.salida_manual ? " (manual)" : "") : "sin salida"), o: (f) => (f.salida ? f.salida.slice(11) : ""), w: 100 },
  { k: "hp", t: "Horas en el piso", v: (f) => f.horas || "—", o: (f) => (f.segundos == null ? -1 : f.segundos), w: 120 },
  { k: "dire", t: "Dirección de entrada", v: (f) => f.direccion_entrada || "—", o: (f) => (f.direccion_entrada || "").toLowerCase(), w: 250 },
  { k: "dirs", t: "Dirección de salida", v: (f) => f.direccion_salida || "—", o: (f) => (f.direccion_salida || "").toLowerCase(), w: 250 },
];

// ── Filtro de columna tipo Excel ────────────────────────────────────────────
function FiltroColumna({ col, valores, sel, onChange, orden, onOrden, onCerrar, pos }) {
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) onCerrar(); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [onCerrar]);
  const visibles = valores.filter((v) => v.toLowerCase().includes(q.trim().toLowerCase()));
  const marcados = sel || new Set(valores);
  const alternar = (v) => {
    const n = new Set(marcados);
    if (n.has(v)) n.delete(v); else n.add(v);
    onChange(n.size === valores.length ? null : n);
  };
  const todosVisibles = visibles.length > 0 && visibles.every((v) => marcados.has(v));
  const marcarVisibles = () => {
    const n = new Set(marcados);
    visibles.forEach((v) => (todosVisibles ? n.delete(v) : n.add(v)));
    onChange(n.size === valores.length ? null : n);
  };
  return (
    <div ref={ref} data-filtro="1" onClick={(e) => e.stopPropagation()} style={{ ...st.pop, position: "fixed", top: pos.top, left: pos.left, zIndex: 4000, marginTop: 0 }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <button onClick={() => onOrden(col.k, 1)} style={{ ...st.btnOrden, ...(orden?.k === col.k && orden.dir === 1 ? st.btnOrdenOn : {}) }}>↑ A→Z</button>
        <button onClick={() => onOrden(col.k, -1)} style={{ ...st.btnOrden, ...(orden?.k === col.k && orden.dir === -1 ? st.btnOrdenOn : {}) }}>↓ Z→A</button>
      </div>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Buscar…" style={st.popBuscar} />
      <label style={{ ...st.opcion, fontWeight: 800, borderBottom: "1px solid #e2e8f0", paddingBottom: 6, marginBottom: 4 }}>
        <input type="checkbox" checked={todosVisibles} onChange={marcarVisibles} /> (Seleccionar todo{q ? " lo buscado" : ""})
      </label>
      <div style={{ maxHeight: 230, overflowY: "auto" }}>
        {visibles.length === 0 && <div style={{ color: "#94a3b8", fontSize: 12.5, padding: 8 }}>Sin resultados</div>}
        {visibles.map((v) => (
          <label key={v} style={st.opcion}>
            <input type="checkbox" checked={marcados.has(v)} onChange={() => alternar(v)} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{v}</span>
          </label>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <button onClick={() => { onChange(null); }} style={st.btnLink}>Borrar filtro</button>
        <button onClick={onCerrar} style={{ ...st.btnLink, color: "#4338ca", fontWeight: 900 }}>Cerrar</button>
      </div>
    </div>
  );
}

// Botones de mapa a la izquierda de cada fila: Google Maps con la ruta «dónde fichó → dónde debía fichar» (entrada y salida)
function BotonesMapa({ f, onMapa }) {
  const hayE = f.mapa?.entrada || f.mapa?.piso;
  const hayS = f.salida && (f.mapa?.salida || f.mapa?.piso);
  if (!hayE && !hayS) return <span style={{ color: "#cbd5e1" }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {hayE && <button onClick={() => onMapa(f, "entrada")} style={st.btnMapaFila} title="Mapa de la ENTRADA: dónde fichó y dónde debía fichar">⬇️</button>}
      {hayS && <button onClick={() => onMapa(f, "salida")} style={{ ...st.btnMapaFila, background: "linear-gradient(135deg,#0ea5e9,#0369a1)" }} title="Mapa de la SALIDA: dónde fichó y dónde debía fichar">⬆️</button>}
    </div>
  );
}

function Direccion({ texto, ok }) {
  if (texto == null) return <span style={{ color: "#94a3b8" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 6, color: ok ? "#15803d" : "#b45309", fontWeight: ok ? 700 : 600, lineHeight: 1.3 }}>
      <span style={{ ...st.punto, background: ok ? "#22c55e" : "#f59e0b" }} />{texto}
    </span>
  );
}

// Mapa (OpenStreetMap + Leaflet): punto rojo = dónde se fichó, verde = dónde debía fichar (el piso), línea recta con la distancia
function MapaModal({ f, cual, onCerrar }) {
  const ref = useRef(null);
  const desde = f.mapa?.[cual];
  const piso = f.mapa?.piso;
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onCerrar]);
  useEffect(() => {
    if (!ref.current || (!desde && !piso)) return undefined;
    const map = L.map(ref.current);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
    const pts = [];
    const punto = (p, color, relleno, texto) => {
      L.circleMarker([p.lat, p.lng], { radius: 9, color, fillColor: relleno, fillOpacity: 0.9, weight: 3 }).addTo(map)
        .bindTooltip(texto, { permanent: true, direction: "top", offset: [0, -8] });
      pts.push([p.lat, p.lng]);
    };
    if (piso) punto(piso, "#15803d", "#22c55e", "Dónde debía fichar (piso)");
    if (desde) punto(desde, "#b91c1c", "#ef4444", "Dónde ha fichado");
    if (piso && desde) {
      const m = Math.round(map.distance([desde.lat, desde.lng], [piso.lat, piso.lng]));
      L.polyline([[desde.lat, desde.lng], [piso.lat, piso.lng]], { color: "#dc2626", weight: 3, dashArray: "8 6" }).addTo(map)
        .bindTooltip(`${m} m`, { permanent: true, direction: "center", className: "fich-dist" });
    }
    if (pts.length > 1) map.fitBounds(L.latLngBounds(pts).pad(0.5), { maxZoom: 19 }); else map.setView(pts[0], 18);
    return () => map.remove();
  }, [desde, piso]);
  return (
    <div onClick={onCerrar} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 18, width: "min(900px, 100%)", boxShadow: "0 20px 50px rgba(0,0,0,0.35)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "#1e3a5f", color: "#fff" }}>
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: 16 }}>🗺️ {f.abreviatura || "Piso"} · {cual === "entrada" ? "⬇️ entrada" : "⬆️ salida"} {dia(cual === "entrada" ? f.entrada : f.salida)} {hora(cual === "entrada" ? f.entrada : f.salida)}</strong>
            <div style={{ fontSize: 12.5, opacity: 0.8 }}>{nombreDe(f)} · {piso?.direccion || "piso sin dirección"}</div>
          </div>
          <button onClick={onCerrar} style={{ border: "none", background: "rgba(255,255,255,0.18)", color: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontWeight: 800 }}>✕</button>
        </div>
        {desde || piso ? <div ref={ref} style={{ height: "min(62vh, 520px)", width: "100%" }} /> : <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Sin coordenadas para el mapa.</div>}
        <div style={{ padding: "8px 16px", fontSize: 12.5, color: "#475569", display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span>🟢 Dónde debía fichar</span><span>🔴 Dónde ha fichado</span>{!desde && <span style={{ color: "#b45309" }}>Sin ubicación al fichar: solo se ve el piso</span>}
        </div>
      </div>
    </div>
  );
}

function Tecnico({ titulo, t, ok }) {
  if (!t) return null;
  const n = (v) => (v == null ? "—" : `${v} m`);
  return (
    <div style={{ flex: 1, minWidth: 220, background: "#fff", border: `2px solid ${ok ? "#bbf7d0" : "#fde68a"}`, borderRadius: 14, padding: "12px 14px" }}>
      <div style={{ fontWeight: 900, color: "#1e3a5f", marginBottom: 8 }}>{titulo} · <span style={{ color: ok ? "#15803d" : "#b45309" }}>{ESTADO_TXT[t.estado] || t.estado}</span></div>
      {[["precision_m", n(t.precision_m)], ["distancia_m", n(t.distancia_m)], ["desv_norte_m", n(t.desv_norte_m)], ["desv_este_m", n(t.desv_este_m)],
        ["margen_m", n(t.margen_m)], ...(t.motivo_ubicacion ? [["motivo_ubicacion", MOTIVO_UBIC_TXT[t.motivo_ubicacion] || t.motivo_ubicacion]] : []), ...(t.intentos_ubic > 1 ? [["intentos_ubic", "2.ª lectura"]] : [])].map(([k, v]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, padding: "3px 0", borderBottom: "1px dashed #e2e8f0" }}>
          <span style={{ color: "#64748b", fontFamily: "monospace" }}>{k}</span><b style={{ color: "#1e293b" }}>{v}</b>
        </div>
      ))}
    </div>
  );
}

// Superadmin: poner la salida a una entrada abierta, o cambiar la hora de una salida manual (queda registro del cambio en la BBDD)
function AccionSalida({ f, onHecho }) {
  const [abierto, setAbierto] = useState(false);
  const [valor, setValor] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState("");
  const puedeCrear = !f.salida && f.id_piso != null;
  const puedeEditar = f.salida_manual && f.id_salida;
  if (!puedeCrear && !puedeEditar) return null;
  const ahora = new Date();
  const maxV = `${iso(ahora)}T${p2(ahora.getHours())}:${p2(ahora.getMinutes())}`;
  const minV = f.entrada.slice(0, 16);
  const abrir = () => { setValor(f.salida ? f.salida.slice(0, 16) : maxV); setErr(""); setAbierto(true); };
  const guardar = async () => {
    setGuardando(true); setErr("");
    try {
      if (puedeEditar) await axios.put(`/api/fichajes/admin/salida/${f.id_salida}`, { fecha_hora: valor });
      else await axios.post("/api/fichajes/admin/salida", { id_entrada: f.id, fecha_hora: valor });
      setAbierto(false); onHecho();
    } catch (e) { setErr(e.response?.data?.error || "No se pudo guardar"); }
    setGuardando(false);
  };
  return (
    <div style={{ background: "#fff", border: "2px solid #fecaca", borderRadius: 14, padding: "12px 14px", minWidth: 260, maxWidth: 420 }}>
      <div style={{ fontWeight: 900, color: "#991b1b", marginBottom: 6 }}>{puedeEditar ? "✋ Salida manual" : "Sin salida"}</div>
      {puedeEditar && f.manual && (
        <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 8 }}>
          {MOTIVO_MANUAL[f.manual.motivo] || "Manual"} · registrada por <b>{f.manual.por || "—"}</b>{f.manual.registrado ? ` el ${dia(f.manual.registrado).split("-").reverse().join("/")} a las ${hora(f.manual.registrado)}` : ""}
        </div>
      )}
      {!abierto ? (
        <button onClick={abrir} style={st.btnAccion}>{puedeEditar ? "✏️ Cambiar la hora de salida" : "➕ Añadir salida"}</button>
      ) : (
        <div>
          <input type="datetime-local" value={valor} min={minV} max={maxV} step={60} onChange={(e) => setValor(e.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 15, fontWeight: 700, borderRadius: 10, border: "2px solid #c7d2fe" }} />
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Entre la entrada ({dia(f.entrada)} {hora(f.entrada)}) y ahora.</div>
          {err && <div style={{ color: "#b91c1c", fontWeight: 700, fontSize: 13, marginTop: 6 }}>{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={() => setAbierto(false)} style={{ ...st.btnAccion, background: "#fff", color: "#475569", border: "2px solid #cbd5e1" }}>Cancelar</button>
            <button onClick={guardar} disabled={guardando || !valor} style={{ ...st.btnAccion, opacity: guardando || !valor ? 0.6 : 1 }}>{guardando ? "Guardando…" : "Guardar"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Percentiles (barras) y dispersión precisión/distancia de lo que se ve en la tabla (entradas y salidas con ubicación)
const pct = (v, q) => { if (!v.length) return null; const a = [...v].sort((x, y) => x - y); const i = (a.length - 1) * q, lo = Math.floor(i); return a[lo] + (a[Math.min(lo + 1, a.length - 1)] - a[lo]) * (i - lo); };
const COL_PT = { dentro: "#22c55e", fuera: "#ef4444", otro: "#f59e0b" };
function GraficosPrecision({ filas, onElegir }) {
  const pts = useMemo(() => filas.flatMap((f) => [f.tecnico_entrada, f.tecnico_salida].filter((t) => t && t.estado !== "manual").map((t) => ({ id: f.id, p: Number(t.precision_m), d: Number(t.distancia_m), e: t.estado, u: f.usuario }))).filter((x) => x.p >= 0 && x.d >= 0 && isFinite(x.p) && isFinite(x.d)), [filas]);
  const [hover, setHover] = useState(null);
  if (!pts.length) return null;
  const QS = [[0.5, "P50"], [0.75, "P75"], [0.9, "P90"], [0.95, "P95"], [1, "Máx"]];
  const barras = (titulo, k, color) => {
    const v = pts.map((x) => x[k]), val = QS.map(([q]) => pct(v, q)), mx = Math.max(...val, 1);
    return (
      <div style={{ flex: "1 1 260px" }}>
        <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 6 }}>{titulo}</div>
        {QS.map(([, n], i) => (
          <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginBottom: 3 }}>
            <span style={{ width: 30, color: "#475569", fontWeight: 700 }}>{n}</span>
            <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 14 }}><div style={{ width: `${(val[i] / mx) * 100}%`, background: color, height: 14, borderRadius: 6 }} /></div>
            <span style={{ width: 56, textAlign: "right", fontWeight: 700, color: "#1e293b" }}>{Math.round(val[i])} m</span>
          </div>
        ))}
      </div>
    );
  };
  const W = 420, H = 260, M = 36;
  const mxP = Math.max(...pts.map((x) => x.p), 20) * 1.05, mxD = Math.max(...pts.map((x) => x.d), 20) * 1.05;
  const X = (v) => M + (v / mxP) * (W - M - 8), Y = (v) => H - M - (v / mxD) * (H - M - 8);
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "12px 16px", marginBottom: 14, display: "flex", flexWrap: "wrap", gap: 22, boxShadow: "0 1px 4px rgba(15,23,42,0.05)" }}>
      {barras("Precisión del GPS (percentiles)", "p", "#0e7490")}
      {barras("Distancia al piso (percentiles)", "d", "#7c3aed")}
      <div style={{ flex: "1 1 420px", maxWidth: 460 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e3a5f", marginBottom: 4 }}>Dispersión: precisión (→) vs distancia (↑) · {pts.length} lecturas</div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", background: "#f8fafc", borderRadius: 10 }}>
          <line x1={M} y1={H - M} x2={W - 8} y2={H - M} stroke="#94a3b8" /><line x1={M} y1={8} x2={M} y2={H - M} stroke="#94a3b8" />
          {[0, 0.5, 1].map((r) => (<g key={r}>
            <text x={M - 4} y={Y(mxD * r) + 3} fontSize="9" textAnchor="end" fill="#64748b">{Math.round(mxD * r)}</text>
            <text x={X(mxP * r)} y={H - M + 12} fontSize="9" textAnchor="middle" fill="#64748b">{Math.round(mxP * r)}</text>
          </g>))}
          <text x={W - 8} y={H - 6} fontSize="9" textAnchor="end" fill="#64748b">precisión (m)</text>
          <text x={4} y={10} fontSize="9" fill="#64748b">distancia (m)</text>
          {pts.map((x, i) => <circle key={i} cx={X(x.p)} cy={Y(x.d)} r={hover === i ? 6 : 3.5} fill={COL_PT[x.e] || COL_PT.otro} fillOpacity="0.65" stroke="#fff" strokeWidth="0.5" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onClick={() => onElegir(x.id)} style={{ cursor: "pointer" }}><title>{`${x.u}: precisión ${Math.round(x.p)} m · distancia ${Math.round(x.d)} m`}</title></circle>)}
        </svg>
        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}><span style={{ color: COL_PT.dentro }}>●</span> dentro <span style={{ color: COL_PT.fuera }}>●</span> fuera <span style={{ color: COL_PT.otro }}>●</span> otros · pulsa un punto para ver ese fichaje en la tabla</div>
      </div>
    </div>
  );
}

export default function Fichajes() {
  const navigate = useNavigate();
  const hoy = iso(new Date());
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [rango, setRango] = useState(false);
  const [filas, setFilas] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [actualizado, setActualizado] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtros, setFiltros] = useState({});   // k -> Set de valores marcados (ausente = todos)
  const [orden, setOrden] = useState({ k: "de", dir: -1 });
  const [menu, setMenu] = useState(null);
  const [mapa, setMapa] = useState(null);
  const [supSel, setSupSel] = useState("");               // "" = todas las supervisoras
  const [menuSup, setMenuSup] = useState(false);
  const [estadoSel, setEstadoSel] = useState("todos");   // todos | dentro | fuera | incid
  const [copiado, setCopiado] = useState("");
  const [abierta, setAbierta] = useState(null);
  const [verGraficos, setVerGraficos] = useState(false);
  const [idSel, setIdSel] = useState(null);      // fichaje elegido en el gráfico de dispersión
  const tablaRef = useRef(null);
  const elegirPunto = (id) => { setIdSel(id); setAbierta(id); setTimeout(() => tablaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  const [ayuda, setAyuda] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true); setError(""); setIdSel(null);
    axios.get("/api/fichajes/lista", { params: { desde, hasta: rango ? hasta : desde } })
      .then((r) => { setFilas(r.data.fichajes || []); setActualizado(new Date()); })
      .catch((e) => { setFilas(null); setError(e.response?.data?.error || "No se pudieron cargar los fichajes."); })
      .finally(() => setCargando(false));
  }, [desde, hasta, rango]);
  useEffect(() => { cargar(); }, [cargar]);

  const mover = (n) => {
    const d = new Date(desde + "T00:00:00"); d.setDate(d.getDate() + n);
    setDesde(iso(d)); setHasta(iso(d));
  };

  const valoresCol = useMemo(() => {
    const out = {};
    COLS.forEach((c) => {
      const m = new Map();
      (filas || []).forEach((f) => { const v = c.v(f); if (!m.has(v)) m.set(v, c.o(f)); });
      out[c.k] = [...m.entries()].sort((a, b) => (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0)).map(([v]) => v);
    });
    return out;
  }, [filas]);

  const visiblesBase = useMemo(() => {
    const q = busca.trim().toLowerCase();
    let r = (filas || []).filter((f) => {
      for (const c of COLS) { const s = filtros[c.k]; if (s && !s.has(c.v(f))) return false; }
      if (supSel && supDe(f) !== supSel) return false;
      if (estadoSel === "manual") { if (!f.salida_manual) return false; }
      else if (estadoSel === "sinsalida") { if (f.salida) return false; }
      else if (estadoSel !== "todos" && claseFila(f) !== estadoSel) return false;
      if (!q) return true;
      return `${f.apellidos} ${f.nombre} ${f.usuario} ${f.abreviatura || ""} ${f.id_empleado || ""} ${f.id_piso || ""} ${f.direccion_entrada || ""} ${f.direccion_salida || ""}`.toLowerCase().includes(q);
    });
    const c = COLS.find((x) => x.k === orden.k);
    if (c) r = [...r].sort((a, b) => { const x = c.o(a), y = c.o(b); return (x < y ? -1 : x > y ? 1 : 0) * orden.dir; });
    return r;
  }, [filas, filtros, busca, orden, estadoSel, supSel]);
  const visibles = useMemo(() => (idSel ? visiblesBase.filter((f) => f.id === idSel) : visiblesBase), [visiblesBase, idSel]);

  const supervisoras = useMemo(() => {
    const m = new Map();
    (filas || []).forEach((f) => { const sp = supDe(f); if (sp) m.set(sp, (m.get(sp) || 0) + 1); });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], "es"));
  }, [filas]);
  const nFiltros = Object.keys(filtros).length;
  const cuenta = useMemo(() => { const c = { todos: 0, dentro: 0, fuera: 0, incid: 0, manual: 0, sinsalida: 0 }; (filas || []).filter((f) => !supSel || supDe(f) === supSel).forEach((f) => { c.todos++; c[claseFila(f)]++; if (f.salida_manual) c.manual++; if (!f.salida) c.sinsalida++; }); return c; }, [filas, supSel]);
  const kpi = useMemo(() => {
    const personas = new Set(visibles.map((f) => f.usuario)).size;
    const fuera = visibles.filter((f) => claseFila(f) === "fuera").length;
    const incid = visibles.filter((f) => claseFila(f) === "incid").length;
    const sinSalida = visibles.filter((f) => !f.salida).length;
    const manuales = visibles.filter((f) => f.salida_manual).length;
    const seg = visibles.reduce((a, f) => a + (f.segundos || 0), 0);
    const lect = (k) => visibles.flatMap((f) => [f.tecnico_entrada?.[k], f.tecnico_salida?.estado === "manual" ? null : f.tecnico_salida?.[k]]).filter((x) => x != null && !isNaN(x)).map(Number);
    const res = (k) => { const v = lect(k); return v.length ? { media: Math.round(v.reduce((x, y) => x + y, 0) / v.length), max: Math.round(Math.max(...v)) } : null; };
    return { personas, fuera, incid, sinSalida, manuales, prec: res("precision_m"), dist: res("distancia_m"), horas: seg ? `${Math.floor(seg / 3600)}h ${p2(Math.floor((seg % 3600) / 60))}m` : "—" };
  }, [visibles]);

  useEffect(() => {
    if (!menu) return undefined;
    const cierra = (e) => { if (e && e.target && e.target.closest && e.target.closest("[data-filtro]")) return; setMenu(null); };
    window.addEventListener("scroll", cierra, true);
    window.addEventListener("resize", cierra);
    return () => { window.removeEventListener("scroll", cierra, true); window.removeEventListener("resize", cierra); };
  }, [menu]);

  const cambiaOrden = (k, dir) => setOrden((o) => (o.k === k && o.dir === dir ? { k: null, dir: 1 } : { k, dir }));
  // Copia al portapapeles lo que se ve (con filtros y orden) como una tabla que Excel pega en celdas
  const copiar = async () => {
    const cab = COLS.map((c) => c.t).join("\t");
    const cuerpo = visibles.map((f) => COLS.map((c) => TSV(c.v(f))).join("\t")).join("\n");
    const texto = `${cab}\n${cuerpo}`;
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(texto);
      else {
        const ta = document.createElement("textarea");
        ta.value = texto; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopiado(`✅ Copiado: ${visibles.length} fila${visibles.length !== 1 ? "s" : ""}`);
    } catch (_) { setCopiado("❌ No se pudo copiar"); }
    setTimeout(() => setCopiado(""), 2500);
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#eef2ff 0%,#f1f5f9 220px)", fontFamily: "Segoe UI, sans-serif" }}>
      <div style={{ background: "linear-gradient(135deg,#1e3a5f,#0f2742)", color: "#fff", padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 4px 14px rgba(15,39,66,0.25)" }}>
        <button onClick={() => navigate(-1)} style={st.btnCab}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Gestión de datos</div>
          <strong style={{ fontSize: 22 }}>📍 Fichajes</strong>
        </div>
        <button onClick={cargar} disabled={cargando} style={{ ...st.btnActualizar, opacity: cargando ? 0.7 : 1 }}>
          <span style={{ display: "inline-block", animation: cargando ? "fichGira 0.9s linear infinite" : "none" }}>🔄</span> {cargando ? "Actualizando…" : "Actualizar"}
        </button>
      </div>
      <style>{"@keyframes fichGira { to { transform: rotate(360deg); } }"}</style>

      <div style={{ padding: "16px 18px", maxWidth: 1800, margin: "0 auto" }}>
        {/* Resumen */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
          {[["Fichajes", filas ? visibles.length : "…", "#1e3a5f", "#fff", "todos"], ["Personas", filas ? kpi.personas : "…", "#4338ca", "#eef2ff", null],
            ["Fuera de sitio", filas ? cuenta.fuera : "…", "#b91c1c", "#fef2f2", "fuera"], ["Con incidencias", filas ? cuenta.incid : "…", "#b45309", "#fff7ed", "incid"],
            ["Sin salida", filas ? cuenta.sinsalida : "…", "#b91c1c", "#fef2f2", "sinsalida"], ["Salida manual", filas ? cuenta.manual : "…", "#9a3412", "#fff7ed", "manual"],
            ["Tiempo total en pisos", filas ? kpi.horas : "…", "#15803d", "#f0fdf4", null],
            [`Precisión media${kpi.prec ? ` (máx ${kpi.prec.max} m)` : ""}`, filas ? (kpi.prec ? `${kpi.prec.media} m` : "—") : "…", "#0e7490", "#ecfeff", null],
            [`Distancia media${kpi.dist ? ` (máx ${kpi.dist.max} m)` : ""}`, filas ? (kpi.dist ? `${kpi.dist.media} m` : "—") : "…", "#0e7490", "#ecfeff", null]].map(([t, v, c, bg, f]) => {
            const act = !!f && estadoSel === f && f !== "todos";
            return (
              <div key={t} onClick={f ? () => setEstadoSel(act || f === "todos" ? "todos" : f) : undefined} title={f ? (f === "todos" ? "Quitar el filtro de estado" : "Pulsa para filtrar la tabla (otra vez para quitarlo)") : undefined}
                style={{ background: bg, border: act ? `3px solid ${c}` : "1px solid #e2e8f0", borderRadius: 16, padding: act ? "8px 14px" : "10px 16px", boxShadow: act ? `0 0 0 3px ${c}33` : "0 1px 4px rgba(15,23,42,0.05)", cursor: f ? "pointer" : "default", userSelect: "none" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: c }}>{t}{act ? " ✓" : ""}</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#1e293b" }}>{v}</div>
              </div>
            );
          })}
        </div>

        {filas && verGraficos && <GraficosPrecision filas={visiblesBase} onElegir={elegirPunto} />}

        {/* Filtros */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "10px 12px", boxShadow: "0 1px 4px rgba(15,23,42,0.05)" }}>
          {!rango ? (
            <div style={st.caja}>
              <button onClick={() => mover(-1)} style={st.flecha} title="Día anterior">◀</button>
              <input type="date" value={desde} max={hoy} onChange={(e) => e.target.value && (setDesde(e.target.value), setHasta(e.target.value))} style={st.fecha} />
              <button onClick={() => mover(1)} disabled={desde >= hoy} style={{ ...st.flecha, opacity: desde >= hoy ? 0.35 : 1 }} title="Día siguiente">▶</button>
            </div>
          ) : (
            <div style={st.caja}>
              <span style={st.lbl}>Desde</span>
              <input type="date" value={desde} max={hasta} onChange={(e) => e.target.value && setDesde(e.target.value)} style={st.fecha} />
              <span style={st.lbl}>hasta</span>
              <input type="date" value={hasta} min={desde} max={hoy} onChange={(e) => e.target.value && setHasta(e.target.value)} style={st.fecha} />
            </div>
          )}
          <button onClick={() => { setRango((r) => !r); setHasta(desde); }} style={{ ...st.chip, ...(rango ? st.chipOn : {}) }}>
            {rango ? "✕ Un solo día" : "📅 Ampliar a rango"}
          </button>
          {(desde !== hoy || hasta !== hoy) && <button onClick={() => { setDesde(hoy); setHasta(hoy); }} style={st.chip}>↩ Hoy</button>}
          {[["todos", "Todos", "#4338ca"], ["dentro", "🟢 Dentro", "#16a34a"], ["fuera", "🔴 Fuera", "#dc2626"], ["incid", "🟠 Incidencias", "#d97706"], ["sinsalida", "⏳ Sin salida", "#b91c1c"], ["manual", "✋ Salida manual", "#9a3412"]].map(([k, tx, c]) => {
            const act = estadoSel === k;
            return (
              <button key={k} onClick={() => setEstadoSel(k)} title={k === "incid" ? "Sin ubicación, QR desconocido o piso sin coordenadas (los «fuera» van aparte)" : k === "fuera" ? "Ficharon fuera del margen del piso" : k === "sinsalida" ? "Entradas que siguen abiertas: aún sin salida" : k === "manual" ? "Salidas puestas a mano (se les olvidó desfichar o cambiaron de piso sin desfichar)" : undefined}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer", border: `2px solid ${act ? c : "#e2e8f0"}`, background: act ? c : "#fff", color: act ? "#fff" : c }}>
                {tx}<span style={{ background: act ? "rgba(255,255,255,0.25)" : `${c}1a`, borderRadius: 999, padding: "0 7px", fontSize: 11.5 }}>{cuenta[k]}</span>
              </button>
            );
          })}
          <span style={{ position: "relative", display: "inline-block" }}>
            <button onClick={() => setMenuSup((v) => !v)} style={{ ...st.chip, display: "inline-flex", alignItems: "center", gap: 8, ...(supSel ? st.chipOn : {}) }}
              title="Filtra por supervisora: sus propios fichajes y los de las asistentas de sus pisos">
              👩‍💼 {supSel || "Todas las supervisoras"} <span style={{ fontSize: 10 }}>{menuSup ? "▲" : "▼"}</span>
            </button>
            {menuSup && (
              <>
                <div onClick={() => setMenuSup(false)} style={{ position: "fixed", inset: 0, zIndex: 25 }} />
                <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 30, minWidth: 250, maxHeight: 340, overflowY: "auto", background: "#fff", border: "1px solid #c7d2fe", borderRadius: 14, boxShadow: "0 12px 32px rgba(15,23,42,0.25)", padding: 6 }}>
                  {[["", "Todas las supervisoras", (filas || []).length], ...supervisoras.map(([n, c]) => [n, n, c])].map(([k, t, c]) => {
                    const act = supSel === k;
                    return (
                      <div key={k || "todas"} onClick={() => { setSupSel(k); setMenuSup(false); }} className="fich-sup"
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: act ? 900 : 600, color: act ? "#4338ca" : "#1e293b", background: act ? "#eef2ff" : "transparent" }}>
                        <span style={{ flex: 1 }}>{act ? "✓ " : ""}{t}</span>
                        <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 999, padding: "0 8px", fontSize: 12, fontWeight: 800 }}>{c}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </span>
          <button onClick={() => setVerGraficos((v) => !v)} style={{ ...st.chip, borderColor: "#a5b4fc", color: "#4338ca" }} title="Percentiles y dispersión de precisión y distancia">📊 Precisión y distancia {verGraficos ? "▴" : "▾"}</button>
          <button onClick={copiar} style={{ ...st.chip, borderColor: "#86efac", color: "#15803d" }} title="Copia la tabla tal como se ve (filtros y orden) para pegarla en Excel">📋 Copiar tabla</button>
          {copiado && <span style={{ fontSize: 13, fontWeight: 800, color: copiado.startsWith("✅") ? "#15803d" : "#b91c1c" }}>{copiado}</span>}

          {(nFiltros > 0 || busca || estadoSel !== "todos" || supSel || idSel) && <button onClick={() => { setFiltros({}); setBusca(""); setEstadoSel("todos"); setSupSel(""); setIdSel(null); }} style={{ ...st.chip, borderColor: "#fca5a5", color: "#b91c1c" }}>✕ Quitar filtros{nFiltros ? ` (${nFiltros})` : ""}</button>}
          <span style={{ position: "relative", display: "inline-block" }}>
            <button onClick={() => setAyuda((v) => !v)} style={{ ...st.chip, ...(ayuda ? st.chipOn : {}) }}>ℹ️ Qué significan los datos</button>
            {ayuda && (
              <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 3500, width: "min(460px, 92vw)", maxHeight: "80vh", overflowY: "auto", background: "#fff", border: "1px solid #c7d2fe", borderRadius: 14, boxShadow: "0 12px 40px rgba(15,23,42,0.35)", padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                  <strong style={{ color: "#1e3a5f" }}>ℹ️ Qué significan los datos</strong>
                  <button onClick={() => setAyuda(false)} style={{ marginLeft: "auto", border: "none", background: "#f1f5f9", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontWeight: 800 }}>✕</button>
                </div>
                {EXPLICACION.map(([k, v]) => <div key={k} style={{ fontSize: 13, padding: "3px 0" }}><b style={{ color: "#1e3a5f", fontFamily: "monospace" }}>{k}:</b> <span style={{ color: "#475569" }}>{v}</span></div>)}
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>Pulsa una fila de la tabla para ver estos datos de ese fichaje.</div>
              </div>
            )}
          </span>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="🔍 Buscar en todo…"
            style={{ flex: "1 1 100%", order: 99, minWidth: 200, padding: "8px 14px", borderRadius: 999, border: "2px solid #c7d2fe", fontSize: 13.5, outline: "none" }} />
        </div>

        {error && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 12, padding: 12, marginBottom: 12 }}>{error}</div>}

        {/* Tabla */}
        <div ref={tablaRef} style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "auto", maxHeight: "68vh", boxShadow: "0 2px 10px rgba(15,23,42,0.06)", opacity: cargando && filas ? 0.6 : 1, transition: "opacity .15s" }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 13, minWidth: "100%" }}>
            <thead>
              <tr>
                <th style={{ ...st.th, background: "#f1f5f9", minWidth: 86 }}>🗺️ Mapa</th>
                {COLS.map((c) => {
                  const activo = !!filtros[c.k];
                  const ordenado = orden.k === c.k;
                  return (
                    <th key={c.k} style={{ ...st.th, minWidth: c.w, background: activo ? "#e0e7ff" : "#f1f5f9" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "space-between" }}>
                        <span onClick={() => cambiaOrden(c.k, ordenado && orden.dir === 1 ? -1 : 1)} style={{ cursor: "pointer", userSelect: "none" }}>
                          {c.t}{ordenado ? (orden.dir === 1 ? " ↑" : " ↓") : ""}
                        </span>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const r = e.currentTarget.getBoundingClientRect();
                          setMenu(menu && menu.k === c.k ? null : { k: c.k, left: Math.max(8, Math.min(r.left, window.innerWidth - 280)), top: Math.max(8, Math.min(r.bottom + 6, window.innerHeight - 400)) });
                        }} title="Filtrar" style={{ ...st.btnFiltro, background: activo ? "#4338ca" : "#fff", color: activo ? "#fff" : "#4338ca" }}>▾</button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filas && visibles.length === 0 && (
                <tr><td colSpan={COLS.length + 1} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                  <div style={{ fontSize: 34 }}>📭</div>No hay fichajes con estos filtros.
                </td></tr>
              )}
              {!filas && !error && <tr><td colSpan={COLS.length + 1} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Cargando…</td></tr>}
              {visibles.map((f, i) => {
                const [bg, barra] = COLOR_FILA[claseFila(f)];
                return (
                  <React.Fragment key={f.id}>
                    <tr onClick={() => setAbierta(abierta === f.id ? null : f.id)} className="fich-fila"
                      style={{ cursor: "pointer", background: bg, boxShadow: `inset 4px 0 0 ${barra}${abierta === f.id ? ", inset 0 0 0 2px #6366f1" : ""}` }}>
                      <td style={{ ...st.td, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                        <BotonesMapa f={f} onMapa={(ff, cual) => setMapa({ f: ff, cual })} />
                      </td>
                      <td style={st.td}>{f.id_empleado ?? "—"}</td>
                      <td style={{ ...st.td, fontWeight: 800, color: "#1e293b", whiteSpace: "nowrap" }}>{nombreDe(f)}</td>
                      <td style={st.td}><span style={st.pill}>{f.rol}</span></td>
                      <td style={st.td}>{f.id_piso ?? "—"}</td>
                      <td style={{ ...st.td, whiteSpace: "nowrap", fontWeight: 700, color: "#1e3a5f" }}>{f.abreviatura || "—"}</td>
                      <td style={st.td}>{dia(f.entrada)}</td>
                      <td style={{ ...st.td, fontWeight: 800 }}>{hora(f.entrada)}</td>
                      <td style={{ ...st.td, fontWeight: 800, whiteSpace: "nowrap" }}>{f.salida ? <>{hora(f.salida)}{dia(f.salida) !== dia(f.entrada) && <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginLeft: 4 }}>({dia(f.salida).slice(8, 10)}/{dia(f.salida).slice(5, 7)})</span>}{f.salida_manual && <span style={{ ...st.pillAviso, marginLeft: 6, color: "#9a3412", background: "#ffedd5", border: "1px solid #fdba74" }} title={MOTIVO_MANUAL[f.manual?.motivo] || "Salida manual"}>✋ manual</span>}</> : <span style={st.pillAviso}>sin salida</span>}</td>
                      <td style={{ ...st.td, whiteSpace: "nowrap" }}>{f.horas ? <span style={st.pillHoras}>🏠 {f.horas}</span> : "—"}</td>
                      <td style={{ ...st.td, minWidth: 220 }}><Direccion texto={f.direccion_entrada} ok={f.entrada_ok} /></td>
                      <td style={{ ...st.td, minWidth: 220 }}><Direccion texto={f.direccion_salida} ok={f.salida_ok} /></td>
                    </tr>
                    {abierta === f.id && (
                      <tr><td colSpan={COLS.length + 1} style={{ padding: "10px 14px 14px", background: "#eef2ff", borderBottom: "1px solid #c7d2fe" }}>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", maxWidth: 900, position: "sticky", left: 12 }}>
                          <Tecnico titulo="Entrada" t={f.tecnico_entrada} ok={f.entrada_ok} />
                          {f.salida_manual && f.tecnico_salida?.distancia_m == null
                            ? <div style={{ flex: 1, minWidth: 220, background: "#fff7ed", border: "2px solid #fdba74", borderRadius: 14, padding: "12px 14px" }}><div style={{ fontWeight: 900, color: "#9a3412" }}>Salida · manual</div><div style={{ fontSize: 13, color: "#7c2d12", marginTop: 6 }}>Sin ubicación: la hora la eligió {f.manual?.motivo === "superadmin" ? "un superadmin" : "la persona"}.</div></div>
                            : <Tecnico titulo={f.salida_manual ? "Salida · manual (dónde la registró)" : "Salida"} t={f.tecnico_salida} ok={f.salida_ok !== false} />}
                          <AccionSalida f={f} onHecho={cargar} />
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <style>{".fich-fila:hover td { filter: brightness(0.95); } .fich-sup:hover { background: #f1f5f9; }"}</style>
        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, display: "flex", flexWrap: "wrap", gap: 14 }}>
          <span>Una fila por entrada (día de entrada dentro del filtro). ✋ manual = la hora de salida la puso la persona (o un superadmin).</span>
          <span style={{ display: "inline-flex", gap: 10, fontSize: 12, color: "#64748b", alignItems: "center" }}>
            {[["dentro", "Dentro"], ["fuera", "Fuera de sitio"], ["incid", "Con incidencias (sin ubicación, QR desconocido…)"]].map(([k, tx]) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: COLOR_FILA[k][0], border: `2px solid ${COLOR_FILA[k][1]}` }} />{tx}</span>
            ))}
          </span>

          {actualizado && <span>Actualizado a las {p2(actualizado.getHours())}:{p2(actualizado.getMinutes())}:{p2(actualizado.getSeconds())}</span>}
        </div>
      </div>
      {menu && (() => {
        const c = COLS.find((x) => x.k === menu.k);
        return (
          <FiltroColumna col={c} valores={valoresCol[c.k] || []} sel={filtros[c.k]} orden={orden} pos={menu}
            onChange={(s) => setFiltros((fl) => { const n = { ...fl }; if (s) n[c.k] = s; else delete n[c.k]; return n; })}
            onOrden={cambiaOrden} onCerrar={() => setMenu(null)} />
        );
      })()}
      {mapa && <MapaModal f={mapa.f} cual={mapa.cual} onCerrar={() => setMapa(null)} />}
      <style>{".fich-dist { background: #fff; border: 1px solid #dc2626; color: #b91c1c; font-weight: 800; }"}</style>
    </div>
  );
}

const st = {
  btnCab: { background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer", padding: "9px 16px" },
  btnActualizar: { background: "#fff", color: "#1e3a5f", border: "none", borderRadius: 999, fontWeight: 900, cursor: "pointer", padding: "10px 20px", fontSize: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.2)" },
  caja: { display: "inline-flex", alignItems: "center", gap: 8, background: "#f8fafc", borderRadius: 12, border: "2px solid #c7d2fe", padding: "4px 8px" },
  lbl: { fontSize: 13, color: "#64748b", fontWeight: 700 },
  flecha: { border: "none", background: "#eef2ff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 900, color: "#3730a3" },
  fecha: { border: "none", fontSize: 14, fontWeight: 700, color: "#1e293b", outline: "none", background: "transparent" },
  chip: { border: "2px solid #c7d2fe", background: "#fff", color: "#3730a3", borderRadius: 999, padding: "6px 14px", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  chipOn: { background: "#4338ca", borderColor: "#4338ca", color: "#fff" },
  th: { position: "sticky", top: 0, zIndex: 3, color: "#475569", fontSize: 12, fontWeight: 800, padding: "10px 8px", borderBottom: "2px solid #c7d2fe", textAlign: "left", whiteSpace: "nowrap" },
  td: { padding: "9px 8px", borderBottom: "1px solid #eef2f7", verticalAlign: "top" },
  btnFiltro: { border: "1.5px solid #c7d2fe", borderRadius: 6, width: 22, height: 22, lineHeight: "18px", cursor: "pointer", fontWeight: 900, fontSize: 12, padding: 0 },
  pop: { position: "absolute", zIndex: 20, marginTop: 6, background: "#fff", border: "1px solid #c7d2fe", borderRadius: 14, boxShadow: "0 12px 32px rgba(15,23,42,0.22)", padding: 12, width: 260, textAlign: "left", fontWeight: 500 },
  popBuscar: { width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 13, outline: "none", marginBottom: 8 },
  opcion: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#1e293b", padding: "4px 2px", cursor: "pointer", whiteSpace: "nowrap" },
  btnOrden: { flex: 1, border: "1.5px solid #c7d2fe", background: "#fff", color: "#3730a3", borderRadius: 8, padding: "5px 0", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  btnOrdenOn: { background: "#4338ca", borderColor: "#4338ca", color: "#fff" },
  btnLink: { border: "none", background: "transparent", color: "#64748b", cursor: "pointer", fontSize: 12.5, fontWeight: 700, padding: 0 },
  pill: { fontSize: 11.5, fontWeight: 800, color: "#4338ca", background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 999, padding: "2px 9px" },
  pillAviso: { fontSize: 11.5, fontWeight: 800, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 999, padding: "2px 9px" },
  pillHoras: { fontSize: 12, fontWeight: 800, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 999, padding: "2px 10px" },
  btnMapaFila: { width: 32, height: 28, border: "none", borderRadius: 999, background: "linear-gradient(135deg,#6366f1,#4338ca)", color: "#fff", fontSize: 14, lineHeight: "28px", padding: 0, cursor: "pointer", boxShadow: "0 1px 4px rgba(67,56,202,0.35)" },
  btnAccion: { flex: 1, border: "none", borderRadius: 10, padding: "9px 12px", fontWeight: 800, fontSize: 13.5, background: "#dc2626", color: "#fff", cursor: "pointer" },
  punto: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginTop: 5 },
};
